import * as Sentry from '@sentry/react';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import i18next from 'i18next';
// eslint-disable-next-line
import packageJson from '../../../../../package.json';
import { Result } from '../../../types/response';
import store from '../../store';
import getBrowserFingerPrint from '../fingerprintUtils';
import { APIError, captureInterfaceError, catchError, isError, regularErrorCodes } from '../index';
import { nativeApi } from '../nativeApi';
import { Platform } from '../nativeApi/interface';
import { getDevToken } from '../nativeApi/utils';
import { SentryErrorType } from '../sentry/capture';
import { isDev } from '../util';

// const STARTSENTRY = String(import.meta.env.VITE_APP_STARTSENTRY) === 'true';
const STARTSENTRY = true;

const source = axios.CancelToken.source();

const u = navigator.userAgent;
const isiOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
const requestPrefix = isiOS ? 'IH' : 'AH';

const getRandomNum = () => String(Math.random()).slice(-6);

const getProductCode = () => (location.pathname.includes('clCredit') ? 'CL' : 'KKC');

const handleConfigs = async (config: any) => {
  const storeToken = store.getState().commonReducer.userToken;
  const { productCode, language } = config;
  const awaitToken = storeToken || (await nativeApi.getToken());

  const devAddtionalHeader = isDev()
    ? {
        // Authorization: awaitToken ? `Bearer ${awaitToken}` : '',
        Authorization: getDevToken(),
      }
    : {
        Authorization: awaitToken ? `Bearer ${awaitToken}` : '',
      };
  const requestID = `${requestPrefix}${new Date().getTime()}${getRandomNum()}`;
  const nativeHeaders = nativeApi.getRequestHeader?.();
  config.headers = {
    ...config.headers,
    ...{
      version: packageJson.version,
      appNameCode: productCode || getProductCode(),
      language: language || i18next.language,
      token: store.getState().commonReducer.userToken, // login接口换取来的token
      requestID,
      vcreditTraceId: requestID,
      client: nativeApi.name,
      sourceChannel:
        nativeApi.name === Platform.MyLink
          ? 'MyLink'
          : nativeApi.name === Platform.Crefit
          ? 'CreFIT'
          : 'H5',
      browserID: await getBrowserFingerPrint(),
      deviceId: (await nativeApi.getDeviceId?.()) || '',
      hardwareId: (await nativeApi.getHardwareId?.()) || '',
    },
    ...nativeHeaders,
    ...devAddtionalHeader,
  };

  if (config.method === 'post') {
    config.data instanceof Array
      ? config.data
      : (config.data = {
          // 如果 productCode 为 null，则不添加 productCode 到请求体
          ...(productCode !== null ? { productCode: productCode || getProductCode() } : {}),
          ...config.data,
        });
  }
  return config;
};

export const createAxiosHttp = (
  _config: AxiosRequestConfig = {},
  baseURL = import.meta.env.VITE_APP_BASE_API
): AxiosInstance => {
  const axiosParams: AxiosRequestConfig = {
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'Cache-Control': 'no-cache',
      'Content-Type': 'application/json;charset=utf-8',
    },
    timeout: 30000,
    baseURL,
    ..._config,
  };
  const request = axios.create(axiosParams);
  request.interceptors.request.use(
    async (config: any) => {
      config.baseURL = baseURL;
      config = handleConfigs(config);
      if (STARTSENTRY) {
        const startTime = new Date();
        // console.log('🚀 ~ startTime: ====== ', startTime);
        config.transaction = Sentry?.startInactiveSpan({
          op: 'http.client',
          name: `${config.method} ${config.url}`,
          forceTransaction: true,
        });
        config.startTime = startTime;
      }
      return config;
    },
    (error) => {
      console.log('出现了问题');
      Promise.reject(error);
    }
  );

  request.interceptors.response.use(
    (response: AxiosResponse<Result>) => {
      const { data, status, config } = response;
      const { msg, code } = data || '';
      const { data: res } = data || '';
      const message = `接口${config.url}报错: ${msg}`;
      // 添加一个接口返回数据的埋点
      if (code === 200) {
        const uploadData = {
          url: config.url,
          requestId: config.headers?.requestID,
          reqdata: JSON.stringify(config.data),
          resdata: JSON.stringify(res),
        };
        sensors.track('Web_Api_Response', uploadData);
        console.log('Web_Api_Response', uploadData);
      } else {
        sensors.track('Web_Api_Error', {
          url: config.url,
          requestId: config.headers?.requestID,
          errorData: JSON.stringify(data),
        });
        console.warn('Web_Api_Error', message);
      }
      if (STARTSENTRY && code !== 200 && !regularErrorCodes.includes(code)) {
        const { startTime, span } = (response.config as any) || {};
        const apiTime = startTime ? new Date().getTime() - startTime.getTime() : 0;
        span?.setAttribute('http.status_code', status);
        span?.setAttribute('http.response_data', JSON.stringify(data));
        span?.finish();
        captureInterfaceError(response, {
          apiTime,
          message,
          errorLevel: 'warn',
        });
      }
      source.cancel();

      if (status === 200 && code === 200) {
        Promise.resolve(res);
        return res;
      }
      return Promise.reject(new APIError(msg, code));
    },
    (error) => {
      if (STARTSENTRY) {
        if (isError(error)) {
          catchError({
            errorType: SentryErrorType.InterfaceError,
            errorObj: error,
            errorLevel: 'error',
          });
        } else if (error.config) {
          const { startTime } = error.config as any;
          const res = error.response;
          const apiTime = startTime ? new Date().getTime() - startTime.getTime() : 0;
          const message = `接口${res.config.url}报错： ${res.msg}`;
          console.log(message, 'message');
          captureInterfaceError(res, {
            apiTime,
            message,
            errorLevel: 'error',
          });
        }
        if (error?.config?.span) {
          error.config.span.finish();
        }
      }
      return Promise.reject(error);
    }
  );
  return request;
};

export default createAxiosHttp();
